import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { MicrosoftState } from './MicrosoftState.js';

/**
 * Represents the state of a user in Microsoft Graph. By comparing the fields in the database with the fields in the Microsoft Graph API response, this entity can determine whether a user is in sync with the Microsoft Graph API.
 */
@Entity()
export class MicrosoftUserState extends MicrosoftState {
  static override _class = 'MicrosoftUserState';

  constructor(doc: PartialEntityData<MicrosoftUserState>) {
    super(doc);
  }

  @Field(type => String)
  personID: Maybe<string>;

  @Field(type => String)
  firstName: Maybe<string>;

  @Field(type => String)
  lastName: Maybe<string>;

  @Field(type => String)
  displayName: Maybe<string>;

  @Field(type => String)
  principalName: Maybe<string>;

  @Field(type => String)
  email: Maybe<string>;

  @Field(type => [String], { default: v => v ?? [] })
  otherEmails: Maybe<string[]>;

  /**
   * The addresses that actually deliver to this person's mailbox.
   *
   * ! This is the only field on either side that answers "does mail sent there arrive". !
   *
   * `email` is the primary and `otherEmails` is a contact field that routes nothing, so until this
   * was read the tab could not show - and nobody could check - whether a person is still reachable
   * under an address they used to have. Entra formats them `SMTP:` for the primary and `smtp:` for
   * the secondaries; the prefix is kept as Entra sends it, because it is the only thing that
   * distinguishes them.
   *
   * Read-only in Graph. Changing it means changing `mail` (which promotes a new primary) or an
   * Exchange operation - `proxyAddresses` itself rejects any patch.
   */
  @Field(type => [String], { default: v => v ?? [] })
  proxyAddresses: Maybe<string[]>;

  @Field(type => Boolean)
  sharedData: Maybe<boolean>;

  @Field(type => Boolean)
  accountEnabled: Maybe<boolean>;

  @Field(type => [String], { default: v => v ?? [] })
  inGroups: Maybe<string[]>;

  @Field(type => [String], { default: v => v ?? [] })
  inGroupIDs: Maybe<string[]>;
}
